import { Suspense, lazy as reactLazy, type ComponentType } from "react";

// Retry lazy chunks once; if a stale deploy removed the chunk, reload the page once.
const lazy = <T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) =>
  reactLazy(async () => {
    try {
      return await factory();
    } catch (err) {
      try {
        return await factory();
      } catch {
        const key = "rentify-chunk-reload";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }
        throw err;
      }
    }
  });
if (typeof window !== "undefined") {
  window.addEventListener("load", () => setTimeout(() => sessionStorage.removeItem("rentify-chunk-reload"), 5000));
}
// Toast components removed — pop-up notifications disabled site-wide
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import ScrollToTop from "@/components/ScrollToTop";
import PageTransition from "@/components/PageTransition";
import PageLoader from "@/components/PageLoader";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import { PayoutSetupRequiredGate } from "@/components/PayoutSetupRequiredGate";
import { RouteSEO } from "@/components/RouteSEO";
import { AnimatePresence } from "framer-motion";


// Eager load only the homepage
import Index from "./pages/Index";

// Lazy load all other pages
const Auth = lazy(() => import("./pages/Auth"));
const Browse = lazy(() => import("./pages/Browse"));
const ItemDetail = lazy(() => import("./pages/ItemDetail"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Earnings = lazy(() => import("./pages/Earnings"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const ListItem = lazy(() => import("./pages/ListItem"));
const Bookings = lazy(() => import("./pages/Bookings"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const SMSTerms = lazy(() => import("./pages/SMSTerms"));
const Support = lazy(() => import("./pages/Support"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Fees = lazy(() => import("./pages/Fees"));
const Contact = lazy(() => import("./pages/Contact"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Messages = lazy(() => import("./pages/Messages"));
const Chat = lazy(() => import("./pages/Chat"));
const SavedSearches = lazy(() => import("./pages/SavedSearches"));
const RentalHistory = lazy(() => import("./pages/RentalHistory"));
const MyListings = lazy(() => import("./pages/MyListings"));
const MyReviews = lazy(() => import("./pages/MyReviews"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Receipt = lazy(() => import("./pages/Receipt"));
const RentalPending = lazy(() => import("./pages/RentalPending"));
const RentalDetails = lazy(() => import("./pages/RentalDetails"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminTestSms = lazy(() => import("./pages/AdminTestSms"));
const Admin = lazy(() => import("./pages/Admin"));
const Settings = lazy(() => import("./pages/Settings"));
const Safety = lazy(() => import("./pages/Safety"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const RentalThankYou = lazy(() => import("./pages/RentalThankYou"));
const NewRequest = lazy(() => import("./pages/NewRequest"));
const MyRequests = lazy(() => import("./pages/MyRequests"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const queryClient = new QueryClient();

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <RouteSEO />
      <Suspense fallback={<PageLoader />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
          <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
          <Route path="/dashboard" element={<PageTransition><Dashboard /></PageTransition>} />
          <Route path="/earnings" element={<PageTransition><Earnings /></PageTransition>} />
          <Route path="/edit-profile" element={<PageTransition><EditProfile /></PageTransition>} />
          <Route path="/browse" element={<PageTransition><Browse /></PageTransition>} />
          <Route path="/item/:id" element={<PageTransition><ItemDetail /></PageTransition>} />
          <Route path="/list-item" element={<PageTransition><ListItem /></PageTransition>} />
          <Route path="/bookings" element={<PageTransition><Bookings /></PageTransition>} />
          <Route path="/privacy" element={<PageTransition><Privacy /></PageTransition>} />
          <Route path="/terms" element={<PageTransition><Terms /></PageTransition>} />
          <Route path="/sms-terms" element={<PageTransition><SMSTerms /></PageTransition>} />
          <Route path="/support" element={<PageTransition><Support /></PageTransition>} />
          <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
          <Route path="/about" element={<PageTransition><About /></PageTransition>} />
          <Route path="/fees" element={<PageTransition><Fees /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/help-center" element={<PageTransition><HelpCenter /></PageTransition>} />
          <Route path="/cart" element={<PageTransition><Cart /></PageTransition>} />
          <Route path="/checkout" element={<PageTransition><Checkout /></PageTransition>} />
          <Route path="/payment-success" element={<PageTransition><PaymentSuccess /></PageTransition>} />
          <Route path="/wishlist" element={<PageTransition><Wishlist /></PageTransition>} />
          <Route path="/messages" element={<PageTransition><Messages /></PageTransition>} />
          <Route path="/chat/:bookingId" element={<PageTransition><Chat /></PageTransition>} />
          <Route path="/saved-searches" element={<PageTransition><SavedSearches /></PageTransition>} />
          <Route path="/rental-history" element={<PageTransition><RentalHistory /></PageTransition>} />
          <Route path="/my-listings" element={<PageTransition><MyListings /></PageTransition>} />
          <Route path="/my-reviews" element={<PageTransition><MyReviews /></PageTransition>} />
          <Route path="/notifications" element={<PageTransition><Notifications /></PageTransition>} />
          <Route path="/receipt/:bookingId" element={<PageTransition><Receipt /></PageTransition>} />
          <Route path="/rental-pending" element={<PageTransition><RentalPending /></PageTransition>} />
          <Route path="/rental/:bookingId" element={<PageTransition><RentalDetails /></PageTransition>} />
          <Route path="/admin/test-sms" element={<PageTransition><AdminTestSms /></PageTransition>} />
          <Route path="/admin" element={<PageTransition><Admin /></PageTransition>} />
          <Route path="/settings" element={<PageTransition><Settings /></PageTransition>} />
          <Route path="/safety" element={<PageTransition><Safety /></PageTransition>} />
          <Route path="/profile/:userId" element={<PageTransition><PublicProfile /></PageTransition>} />
          <Route path="/rental-thank-you/:bookingId" element={<PageTransition><RentalThankYou /></PageTransition>} />
          <Route path="/requests/new" element={<PageTransition><NewRequest /></PageTransition>} />
          <Route path="/my-requests" element={<PageTransition><MyRequests /></PageTransition>} />
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
          <Route path="/unsubscribe" element={<PageTransition><Unsubscribe /></PageTransition>} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <CartProvider>
          <WishlistProvider>
            {/* Toast notifications removed per design preference */}
            <BrowserRouter>
              <ScrollToTop />
              <AuthProvider>
                <AppErrorBoundary>
                  <PayoutSetupRequiredGate />
                  <AnimatedRoutes />
                </AppErrorBoundary>
              </AuthProvider>
            </BrowserRouter>
          </WishlistProvider>
        </CartProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
